import { useTranslation } from 'react-i18next';
import {
  Bar,
  CartesianGrid,
  Cell,
  ComposedChart,
  LabelList,
  ReferenceArea,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis
} from 'recharts';
import { Card, CardContent, CardHeader } from '../../elements/ui/card';
import { Skeleton } from '../../elements/ui/skeleton';
import { ChartContainer, type ChartConfig } from '../../elements/ui/chart';
import { ChartHoverCard } from '../../elements/ui/hover-card';
import { useFarmDateTime } from '../../hooks/useFarmDateTime';
import { CHART_GROW_MS, useFirstDrawAnimation, useRevealOnScroll } from '../../hooks/useMotion';
import { busyDimClass } from '../../elements/ui/motion';
import { SlaughterForecastWeek, isDeliveryBelowExpected } from '../../models/slaughter-forecast';
import { slaughterForecastColors as c } from './colors';

interface ChartRow extends SlaughterForecastWeek {
  label: string;
  isCurrent: boolean;
  belowExpected: boolean;
  /** `expected` on a forecast week only - the one bar with no delivered bar beside it to carry a number. */
  forecastExpected: number | null;
}

const num = (value: number) => value.toLocaleString();
const signed = (value: number) => (value > 0 ? `+${num(value)}` : value < 0 ? `−${num(-value)}` : '0');

const FORECAST_PATTERN_ID = 'slaughter-forecast-stripes';

const chartConfig: ChartConfig = {
  expected: { label: 'Expected', color: c.expected },
  delivered: { label: 'Delivered', color: c.delivered }
};

const TooltipRow = ({ label, value, dotColor }: { label: string; value: string; dotColor?: string }) => (
  <div className="flex items-center justify-between gap-6">
    <span className="flex items-center gap-1.5 text-gray-500">
      {dotColor && <span className="h-1.5 w-1.5 shrink-0 rounded-full" style={{ backgroundColor: dotColor }} />}
      {label}
    </span>
    <span className="font-medium tabular-nums text-gray-900">{value}</span>
  </div>
);

interface WeekTooltipProps {
  active?: boolean;
  payload?: { payload: ChartRow }[];
  hasBirthCohorts: boolean;
}

/**
 * The lower half names the cohort a week's expectation comes from, so a farmer can tell a bad delivery
 * week from a bad farrowing week 24 weeks earlier - the whole point of putting the two side by side.
 */
function WeekTooltip({ active, payload, hasBirthCohorts }: WeekTooltipProps) {
  const { t } = useTranslation();
  const { formatDate } = useFarmDateTime();

  if (!active || !payload?.length) return null;

  const week = payload[0].payload;
  const difference = week.expected !== null && week.delivered !== null ? week.delivered - week.expected : null;

  return (
    <ChartHoverCard className="min-w-60">
      <p className="mb-1.5 font-medium text-gray-700">
        {t('f-slaughter-tooltip-week', { week: week.weekNumber, date: formatDate(week.weekStartDate) })}
      </p>

      <div className="grid gap-1">
        {hasBirthCohorts && (
          <TooltipRow
            label={t('f-slaughter-expected')}
            value={week.expected === null ? '–' : num(week.expected)}
            dotColor={c.expected}
          />
        )}
        <TooltipRow
          label={t('f-slaughter-delivered')}
          value={week.delivered === null ? t('f-slaughter-tooltip-not-yet') : num(week.delivered)}
          dotColor={week.belowExpected ? c.belowExpected : c.delivered}
        />
        {difference !== null && <TooltipRow label={t('f-slaughter-tooltip-difference')} value={signed(difference)} />}
      </div>

      {/* A farm that buys its piglets in has no cohort to name. */}
      {hasBirthCohorts && (
        <>
          <p className="mt-2 mb-1 border-t border-gray-100 pt-2 font-medium text-gray-700">
            {t('f-slaughter-tooltip-cohort', {
              week: week.birthWeekNumber,
              date: formatDate(week.birthWeekStartDate)
            })}
          </p>

          {week.pigletsRegistered === null ? (
            <p className="max-w-60 text-gray-500">{t('f-slaughter-tooltip-predates')}</p>
          ) : (
            <TooltipRow label={t('f-slaughter-tooltip-registered')} value={num(week.pigletsRegistered)} />
          )}
        </>
      )}
    </ChartHoverCard>
  );
}

function ChartLegendRow({ showExpected }: { showExpected: boolean }) {
  const { t } = useTranslation();

  return (
    <div className="flex flex-wrap items-center gap-4 text-xs text-gray-600">
      {showExpected && (
        <span className="flex items-center gap-1.5">
          <span className="h-2.5 w-2.5 rounded-[2px]" style={{ backgroundColor: c.expected }} />
          {t('f-slaughter-legend-expected')}
        </span>
      )}
      <span className="flex items-center gap-1.5">
        <span className="h-2.5 w-2.5 rounded-[2px]" style={{ backgroundColor: c.delivered }} />
        {t('f-slaughter-legend-delivered')}
      </span>
      {showExpected && (
        <>
          <span className="flex items-center gap-1.5">
            <span className="h-2.5 w-2.5 rounded-[2px]" style={{ backgroundColor: c.belowExpected }} />
            {t('f-slaughter-legend-below')}
          </span>
          <span className="flex items-center gap-1.5">
            <span
              className="h-2.5 w-2.5 rounded-[2px] border border-gray-300"
              style={{
                backgroundImage: `repeating-linear-gradient(135deg, ${c.forecastStripe} 0 1.5px, transparent 1.5px 4px)`
              }}
            />
            {t('f-slaughter-legend-forecast')}
          </span>
        </>
      )}
    </div>
  );
}

/** Week label, with the running week marked - its delivered bar is still filling up. */
const WeekTick = ({
  x,
  y,
  payload,
  rows
}: {
  x?: number;
  y?: number;
  payload?: { value: string };
  rows: ChartRow[];
}) => {
  const { t } = useTranslation();
  const row = rows.find((candidate) => candidate.label === payload?.value);

  return (
    <g transform={`translate(${x},${y})`}>
      <text
        dy={12}
        textAnchor="middle"
        fontSize={12}
        fill={row?.isCurrent ? c.ink : '#6b7280'}
        fontWeight={row?.isCurrent ? 600 : 400}
      >
        {payload?.value}
      </text>
      {row?.isCurrent && (
        <text dy={26} textAnchor="middle" fontSize={10} fill="#6b7280">
          {t('f-slaughter-now')}
        </text>
      )}
    </g>
  );
};

/** See the note on the piglet chart's copy: rounds the axis top to a 1/2/2.5/5 x 10^n step. */
const buildYAxisScale = (maxValue: number): { max: number; ticks: number[] } => {
  if (maxValue <= 0) return { max: 10, ticks: [0, 5, 10] };

  const targetIntervals = 4;
  const rawStep = maxValue / targetIntervals;
  const magnitude = 10 ** Math.floor(Math.log10(rawStep));
  const step = [1, 2, 2.5, 5, 10].map((m) => m * magnitude).find((candidate) => candidate >= rawStep) ?? 10 * magnitude;
  const max = Math.ceil(maxValue / step) * step;

  const ticks: number[] = [];
  for (let value = 0; value <= max + step / 2; value += step) {
    ticks.push(Math.round(value));
  }

  return { max, ticks };
};

const ChartSkeleton = () => (
  <div className="flex h-full w-full items-end gap-2 px-4 pb-6">
    {[55, 75, 45, 85, 65, 90, 50, 70, 80, 60, 40, 72, 66, 58].map((height, i) => (
      <Skeleton key={i} className="flex-1 rounded-t-sm rounded-b-none" style={{ height: `${height}%` }} />
    ))}
  </div>
);

export interface SlaughterDeliveryChartProps {
  weeks?: SlaughterForecastWeek[] | null;
  hasBirthCohorts: boolean;
  slaughterAgeWeeks: number;
  mortalityPercent: number;
  isBusy: boolean;
}

export default function SlaughterDeliveryChart({
  weeks,
  hasBirthCohorts,
  slaughterAgeWeeks,
  mortalityPercent,
  isBusy
}: SlaughterDeliveryChartProps) {
  const { t } = useTranslation();

  const source = weeks ?? [];
  const lastPastIndex = source.reduce((last, week, index) => (week.isForecast ? last : index), -1);

  const rows: ChartRow[] = source.map((week, index) => ({
    ...week,
    label: t('f-slaughter-week-short', { week: week.weekNumber }),
    isCurrent: index === lastPastIndex,
    belowExpected: isDeliveryBelowExpected(week),
    forecastExpected: week.isForecast ? week.expected : null
  }));

  const forecastRows = rows.filter((row) => row.isForecast);
  const yScale = buildYAxisScale(Math.max(0, ...rows.map((row) => Math.max(row.expected ?? 0, row.delivered ?? 0))));

  // Drawn once it scrolls into view, so the bars grow in where the reader can see them. A reload keeps the
  // previous chart on screen, dimmed, rather than dropping back to the skeleton.
  const { ref: plotRef, revealed } = useRevealOnScroll();
  const animateFirstDraw = useFirstDrawAnimation(revealed && rows.length > 0);

  return (
    <Card>
      <CardHeader className="space-y-1 pb-4">
        <h3 className="text-base font-semibold text-gray-900">{t('f-slaughter-chart-title')}</h3>
        <p className="text-xs text-gray-500">
          {hasBirthCohorts
            ? t('f-slaughter-chart-subtitle', { weeks: slaughterAgeWeeks, mortality: mortalityPercent })
            : t('f-slaughter-chart-subtitle-no-cohorts')}
        </p>
        <div className="pt-2">
          <ChartLegendRow showExpected={hasBirthCohorts} />
        </div>
      </CardHeader>

      <CardContent>
        <div ref={plotRef} className="h-80 w-full">
          {isBusy && rows.length === 0 ? (
            <ChartSkeleton />
          ) : rows.length === 0 ? (
            <div className="flex h-full items-center justify-center text-sm text-gray-400">
              {t('f-slaughter-empty')}
            </div>
          ) : !revealed ? null : (
            <ChartContainer
              config={chartConfig}
              resizeDebounce={250}
              className={`h-full w-full ${busyDimClass(isBusy)}`}
            >
              <ComposedChart
                data={rows}
                margin={{ top: 24, right: 8, bottom: 16, left: 0 }}
                barGap={2}
                barCategoryGap="22%"
                maxBarSize={22}
              >
                <defs>
                  <pattern
                    id={FORECAST_PATTERN_ID}
                    width={6}
                    height={6}
                    patternUnits="userSpaceOnUse"
                    patternTransform="rotate(45)"
                  >
                    <rect width={6} height={6} fill="#f3f4f6" />
                    <line x1={0} y1={0} x2={0} y2={6} stroke={c.forecastStripe} strokeWidth={2} />
                  </pattern>
                </defs>

                <CartesianGrid vertical={false} stroke={c.grid} />

                {/* The weeks ahead sit on a band of their own, so the missing delivered bar reads as "not
                    yet" rather than as a week where nothing was delivered. */}
                {forecastRows.length > 0 && (
                  <ReferenceArea
                    x1={forecastRows[0].label}
                    x2={forecastRows[forecastRows.length - 1].label}
                    fill={c.forecastBand}
                    fillOpacity={1}
                    ifOverflow="extendDomain"
                    label={{
                      value: t('f-slaughter-forecast-band'),
                      position: 'insideTop',
                      fill: '#6b7280',
                      fontSize: 11,
                      offset: -18
                    }}
                  />
                )}

                <XAxis
                  dataKey="label"
                  tickLine={false}
                  axisLine={false}
                  interval={0}
                  height={36}
                  tick={<WeekTick rows={rows} />}
                />
                <YAxis
                  allowDecimals={false}
                  domain={[0, yScale.max]}
                  ticks={yScale.ticks}
                  tickLine={false}
                  axisLine={false}
                  width={48}
                  tick={{ fontSize: 12, fill: '#9ca3af' }}
                />

                <RechartsTooltip
                  cursor={false}
                  isAnimationActive={false}
                  content={<WeekTooltip hasBirthCohorts={hasBirthCohorts} />}
                />

                {hasBirthCohorts && (
                  <Bar
                    dataKey="expected"
                    radius={[4, 4, 0, 0]}
                    isAnimationActive={animateFirstDraw}
                    animationDuration={CHART_GROW_MS}
                    animationEasing="ease-out"
                  >
                    {rows.map((row) => (
                      <Cell
                        key={`${row.year}-${row.weekNumber}`}
                        fill={row.isForecast ? `url(#${FORECAST_PATTERN_ID})` : c.expected}
                        stroke={row.isForecast ? c.forecastStripe : undefined}
                        strokeDasharray={row.isForecast ? '3 2' : undefined}
                      />
                    ))}
                    {/* Labelled only where there is no delivered bar beside it to carry the number. */}
                    <LabelList
                      dataKey="forecastExpected"
                      position="top"
                      offset={8}
                      fill="#6b7280"
                      fontSize={11}
                      fontWeight={600}
                      formatter={(value) => (value === null || value === undefined ? '' : num(Number(value)))}
                    />
                  </Bar>
                )}

                <Bar
                  dataKey="delivered"
                  radius={[4, 4, 0, 0]}
                  isAnimationActive={animateFirstDraw}
                  animationDuration={CHART_GROW_MS}
                  animationEasing="ease-out"
                >
                  {rows.map((row) => (
                    <Cell
                      key={`${row.year}-${row.weekNumber}`}
                      fill={row.belowExpected ? c.belowExpected : c.delivered}
                    />
                  ))}
                  <LabelList
                    dataKey="delivered"
                    position="top"
                    offset={8}
                    fill={c.ink}
                    fontSize={11}
                    fontWeight={600}
                    formatter={(value) => (value === null || value === undefined ? '' : num(Number(value)))}
                  />
                </Bar>
              </ComposedChart>
            </ChartContainer>
          )}
        </div>

        {/* The delivered side only holds slaughter results matched to an animal, so a shortfall can be a
            data gap rather than a missing pig. Said once, under the plot, rather than on every bar. */}
        <p className="mt-3 text-xs text-gray-500">{t('f-slaughter-matching-gap')}</p>
      </CardContent>
    </Card>
  );
}
